// Derives the admin-grid columns from the raw imported data, replicating the
// spreadsheet formulas (Sheet1 computed columns + Sheet2 "Ref" lookups).
// Portal/payor mapping is intentionally omitted for now.

/* ---- Ref tab: sLocation -> { code, group } ---- */
export const REF_LOCATION = {
  "anaheim": { code: "AN", group: "LACN" },
  "anaheim satellite": { code: "AS", group: "LACN" },
  "burbank": { code: "BU", group: "LACN" },
  "century city": { code: "CC", group: "LACN" },
  "corona": { code: "CCCMG (COR)", group: "CCCMG" },
  "fountain valley": { code: "CCCMG (FV)", group: "CCCMG" },
  "glendale adventist": { code: "GA", group: "LACN" },
  "glendale memorial": { code: "GM", group: "LACN" },
  "good samiritan": { code: "GS", group: "LACN" },
  "hollywood presbyterian": { code: "HP", group: "LACN" },
  "laguna hills": { code: "CCCMG (LN)", group: "CCCMG" },
  "mission hills": { code: "MH", group: "LACN" },
  "montebello": { code: "MO", group: "LACN" },
  "northridge": { code: "NOR", group: "LACN" },
  "pasadena": { code: "PA", group: "LACN" },
  "pomona": { code: "PO", group: "LACN" },
  "riverside": { code: "CCCMG (RIV)", group: "CCCMG" },
  "surg -glendale adventist": { code: "Surgical", group: "Surgical" },
  "surg- los alamitos": { code: "Surgical", group: "Surgical" },
  "surg-torrance": { code: "Surgical", group: "Surgical" },
  "valencia": { code: "VA", group: "LACN" },
  "valley pres": { code: "VP", group: "LACN" },
  "van nuys": { code: "VP", group: "LACN" },
  "wilshire": { code: "GS", group: "LACN" },
  "memorial": { code: "MM", group: "LACN" },
  "tarzana": { code: "TAR", group: "LACN" },
  "torrance": { code: "TOR", group: "LACN" },
  "los alamitos": { code: "CCCMG (LA)", group: "CCCMG" },
};

/* ---- Ref tab: Textbox134 -> group ---- */
export const REF_TEXTBOX = { "radiology": "PHS", "activity": "LACN", "lab test": "PHS" };

/* ---- dropdown option lists from the Ref tab (for the editable dialog later) ---- */
export const STATUS_OPTIONS = ["Approved", "Canceled", "Denied", "Reviewed", "Pending", "Withdraw"];
export const NEED_HELP_OPTIONS = ["Yes", "No"];
export const USER_GROUP_OPTIONS = ["PHS", "LACN", "CCCMG"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ---- runtime overrides loaded from the DB (/api/ref); fall back to the built-in maps above ---- */
let LOC = REF_LOCATION;
let TB = REF_TEXTBOX;
let PORTAL = {};   // payor name -> portal name
let OPTIONS = { STATUS: STATUS_OPTIONS, NEED_HELP: NEED_HELP_OPTIONS, USER_GROUP: USER_GROUP_OPTIONS };

/** Apply reference data fetched from the backend. Safe to call with partial/empty data. */
export function applyRefData(ref) {
  if (!ref) return;
  if (Array.isArray(ref.locations) && ref.locations.length) {
    const loc = {}, tb = {};
    ref.locations.forEach((l) => {
      const key = (l.sourceName || "").trim().toLowerCase();
      if (l.kind === "TEXTBOX") tb[key] = l.userGroup;
      else loc[key] = { code: l.code || "", group: l.userGroup };
    });
    LOC = loc; TB = tb;
  }
  if (ref.options) OPTIONS = { ...OPTIONS, ...ref.options };
  if (ref.payorPortal) {
    PORTAL = {};
    Object.entries(ref.payorPortal).forEach(([payor, portal]) => { PORTAL[String(payor).trim().toLowerCase()] = portal; });
  }
}
export const getOptions = () => OPTIONS;

/* ---- helpers ---- */
const norm = (s) => (s == null ? "" : String(s).trim().toLowerCase());

/** Read a raw column value from the record's stored data (exact, then loose match). */
export function raw(r, name) {
  const d = r.data || {};
  if (d[name] != null) return d[name];
  const target = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  for (const k of Object.keys(d)) {
    if (k.toLowerCase().replace(/[^a-z0-9]/g, "") === target) return d[k];
  }
  return "";
}

/** Parse ISO / US-date / Excel-serial into a local midnight Date, or null. */
export function parseDate(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number" || /^\d+(\.\d+)?$/.test(String(v).trim())) {
    const n = parseFloat(v);
    if (n > 20000 && n < 80000) { // Excel serial
      const ms = Math.round((n - 25569) * 86400000);
      const d = new Date(ms);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    }
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
  const d = new Date(s);
  return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const fmtDate = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : "");
const fmtMonth = (d) => (d ? `${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(2)}` : "");
const daysFromToday = (d) => {
  const t = new Date(); const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((d - today) / 86400000);
};

/** RTC Priority: 0 if past, then 1..12 per 7-day band, else 13. */
function rtcPriority(rtc) {
  if (!rtc) return "";
  const delta = daysFromToday(rtc);
  if (delta < 0) return 0;
  for (let w = 1; w <= 12; w++) if (delta <= 7 * w) return w;
  return 13;
}
/** RTCStatus: same 7-day bands as text. */
function rtcStatus(rtc) {
  if (!rtc) return "";
  const delta = daysFromToday(rtc);
  if (delta < 0) return "Lapsed";
  for (let w = 1; w <= 12; w++) if (delta <= 7 * w) return `${w} Wk${w === 1 ? "" : "s"}`;
  return ">12 Wks";
}

/** Location classification (Sheet1 col C). */
// Known CCCMG clinics — a safety net so they never fall through to LACN.
const CCCMG_RE = /corona|fountain\s*valley|laguna\s*hills|riverside|los\s*alamitos/i;

function location(r) {
  const sLoc = raw(r, "sLocation");
  const comp = r.orderName || raw(r, "sComponentName");
  const ins = raw(r, "PrimaryInsName");
  const tb = raw(r, "Textbox134");
  // NOTE: Excluded-MD / GI-procedure records keep their REAL group here; the "Excluded MD" tab is a separate
  // server-side flag (excludedMd), so the group shown to admins never changes.
  if (/surg/i.test(sLoc)) return "Surgical";
  if (/ca 27-29/i.test(comp)) return "Auth Not Needed";
  if (/ca 15-3/i.test(comp)) return "Auth Not Needed";
  // Straight Medicare payors — "Medicare (Medicare)" and "Medicare Northern CA" — are Auth Not Needed. Medicare
  // ADVANTAGE plans (IPA-managed, e.g. "… Medicare Advantage") still need auth and route by location.
  if (norm(ins) === "medicare (medicare)" || norm(ins) === "medicare northern ca") return "Auth Not Needed";
  // A payor mapped to an "Auth Not Needed" portal is Auth Not Needed too.
  const _portal = PORTAL[norm(ins)];
  if (_portal && String(_portal).toLowerCase().includes("auth not needed")) return "Auth Not Needed";
  if (CCCMG_RE.test(sLoc)) return "CCCMG";
  const grp = LOC[norm(sLoc)]?.group;
  if (grp) return grp;                 // trust the mapping (LACN / CCCMG / Surgical / …)
  const tbGrp = TB[norm(tb)];
  if (tbGrp === "LACN") return "LACN";
  return "PHS";                         // unmatched never defaults to LACN
}

/** Compute every derived grid column for one record. */
export function derive(r) {
  const orderDate = parseDate(r.orderDate);
  const rtc = parseDate(raw(r, "dStartDateTime"));
  const sValue = raw(r, "sValue");
  const sDate = raw(r, "sDate");
  const loc = location(r);
  // Some clinics store their code as "GROUP (CODE)" (e.g. "CCCMG (RIV)"); show just the code part.
  const rawCode = LOC[norm(raw(r, "sLocation"))]?.code || "";
  const codeMatch = rawCode.match(/^\s*[A-Za-z]+\s*\((.+?)\)\s*$/);
  const cleanCode = codeMatch ? codeMatch[1] : rawCode;
  return {
    urgentRoutine: /urgent/i.test(String(sValue)) ? "Urgent" : "Routine",
    rtcPriority: rtcPriority(rtc),
    clinicPriority: r.priorityOverride === "STAT" ? "STAT" : (/asap|stat/i.test(String(sDate)) ? "STAT" : "Non-STAT"),
    mrn: r.mrn,
    location: loc,
    location2: cleanCode,
    clinic: raw(r, "sLocation") || "",
    locationCode: cleanCode,
    clinicGroup: LOC[norm(raw(r, "sLocation"))]?.group || "",
    orderMD: raw(r, "OrderMD") || "",
    insurance: raw(r, "PrimaryInsName"),
    orders: r.orderName,
    cpt: raw(r, "sCPTCode"),
    portal: PORTAL[String(raw(r, "PrimaryInsName") || "").trim().toLowerCase()] || "",   // from payor→portal mapping
    assignedUserGroup: loc,
    orderDate: fmtDate(orderDate),
    orderMonth: fmtMonth(orderDate),
    rtcDate: fmtDate(rtc),
    rtcMonth: fmtMonth(rtc),
    rtcStatus: rtcStatus(rtc),
  };
}